/**
 * KEIKKA LocaleManager v9.0 - WORLD-CLASS i18n
 * 
 * THIS IS THE ONLY FILE THAT MANAGES LOCALE STATE.
 * Single source of truth for ALL locale operations.
 * 
 * PRIORITY ORDER (strict):
 * 1. URL ?lang=xx (highest - deep link override)
 * 2. localStorage (keikka_lang)
 * 3. Cookie (keikka_lang)
 * 4. Browser language (navigator.language)
 * 5. Default 'en'
 * 
 * STORAGE:
 * - localStorage key: keikka_lang
 * - Cookie name: keikka_lang
 * - BroadcastChannel: keikka-i18n
 */

// =============================================================================
// SUPPORTED LOCALES - ALL languages with translation files
// =============================================================================
export const SUPPORTED_LOCALES = [
  // Nordic / Baltic region (primary markets)
  { code: 'et', label: 'Estonian', nativeLabel: 'Eesti', flag: '🇪🇪' },
  { code: 'en', label: 'English', nativeLabel: 'English', flag: '🇬🇧' },
  { code: 'fi', label: 'Finnish', nativeLabel: 'Suomi', flag: '🇫🇮' },
  { code: 'sv', label: 'Swedish', nativeLabel: 'Svenska', flag: '🇸🇪' },
  { code: 'nb', label: 'Norwegian', nativeLabel: 'Norsk Bokmål', flag: '🇳🇴' },
  { code: 'ru', label: 'Russian', nativeLabel: 'Русский', flag: '🇷🇺' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español', flag: '🇪🇸' },
];

// Backwards-compatible aliases
export const ALL_LANGUAGES = SUPPORTED_LOCALES;
export const TIER_A_LANGUAGES = SUPPORTED_LOCALES.map(l => l.code);

// Language lookup map
export const LANGUAGE_MAP = Object.fromEntries(
  SUPPORTED_LOCALES.map(lang => [lang.code, {
    ...lang,
    name: lang.label,
    nativeName: lang.nativeLabel,
    tier: 'A',
  }])
);

// RTL languages (for future expansion)
export const RTL_LANGUAGES = ['ar', 'he', 'fa'];

// =============================================================================
// CONSTANTS - SINGLE SOURCE OF TRUTH
// =============================================================================
export const STORAGE_KEY = 'keikka_lang';           // localStorage
export const COOKIE_NAME = 'keikka_lang';           // Cookie
export const BROADCAST_CHANNEL = 'keikka-i18n';     // BroadcastChannel
export const DEFAULT_LANGUAGE = 'en';
export const FALLBACK_LANGUAGE = 'en';

// Legacy compatibility exports
export const DEFAULT_LOCALE = DEFAULT_LANGUAGE;
export const FALLBACK_LOCALE = FALLBACK_LANGUAGE;

// =============================================================================
// STATE
// =============================================================================
let currentLocale = null;
let broadcastChannel = null;
let changeListeners = [];
let isInitialized = false;

// =============================================================================
// LANGUAGE ALIASES (normalize regional variants)
// =============================================================================
const LOCALE_ALIASES = {
  // Norwegian variants
  'no': 'nb',
  'nb-no': 'nb',
  'nn': 'nb',
  'nn-no': 'nb',
  // Estonian
  'et-ee': 'et',
  // Finnish
  'fi-fi': 'fi',
  // Swedish
  'sv-se': 'sv',
  'sv-fi': 'sv',
  // Russian
  'ru-ru': 'ru',
  // English variants
  'en-us': 'en',
  'en-gb': 'en',
  'en-au': 'en',
  'en-ca': 'en',
  // Spanish variants
  'es-es': 'es',
  'es-mx': 'es',
  'es-ar': 'es',
  'es-co': 'es',
  'es-cl': 'es',
  'es-419': 'es',
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Normalize a language code to one we support
 * Returns null if language is not supported
 */
export function normalizeLocale(locale) {
  if (!locale || typeof locale !== 'string') return null;
  
  const lower = locale.toLowerCase().trim();
  
  // Check aliases first
  if (LOCALE_ALIASES[lower]) {
    return LOCALE_ALIASES[lower];
  }
  
  // Check if exact match in supported languages
  if (TIER_A_LANGUAGES.includes(lower)) {
    return lower;
  }
  
  // Extract base code and check
  const base = lower.split('-')[0].split('_')[0];
  if (TIER_A_LANGUAGES.includes(base)) {
    return base;
  }
  
  return null;
}

// Alias for compatibility
export const normalizeLanguage = normalizeLocale;

/**
 * Check if a locale is supported
 */
export function isLocaleSupported(locale) {
  return normalizeLocale(locale) !== null;
}

/**
 * Check if locale has full translation files
 */
export function isTierA(locale) {
  const normalized = normalizeLocale(locale);
  return normalized !== null;
}

// Alias
export const isPrimaryLocale = isTierA;

/**
 * Check if locale is RTL
 */
export function isRTL(locale) {
  const normalized = normalizeLocale(locale);
  return normalized !== null && RTL_LANGUAGES.includes(normalized);
}

/**
 * Get text direction
 */
export function getTextDirection(locale) {
  return isRTL(locale) ? 'rtl' : 'ltr';
}

/**
 * Get language info object
 */
export function getLocaleInfo(locale) {
  const normalized = normalizeLocale(locale);
  if (normalized && LANGUAGE_MAP[normalized]) {
    return LANGUAGE_MAP[normalized];
  }
  return LANGUAGE_MAP[DEFAULT_LANGUAGE];
}

// Alias
export const getLanguageInfo = getLocaleInfo;

/**
 * Search languages by query
 */
export function searchLanguages(query) {
  if (!query || query.trim().length === 0) {
    return SUPPORTED_LOCALES;
  }
  
  const q = query.toLowerCase().trim();
  return SUPPORTED_LOCALES.filter(lang =>
    lang.code.toLowerCase().includes(q) ||
    lang.label.toLowerCase().includes(q) ||
    lang.nativeLabel.toLowerCase().includes(q)
  );
}

// =============================================================================
// COOKIE MANAGEMENT
// =============================================================================

function getCookieDomain() {
  if (typeof window === 'undefined') return null;
  
  const hostname = window.location.hostname;
  if (hostname.endsWith('keikka.work')) {
    return '.keikka.work';
  }
  return null;
}

function setLocaleCookie(locale) {
  if (!locale || typeof document === 'undefined') return;
  
  try {
    const maxAge = 365 * 24 * 60 * 60; // 1 year
    const domain = getCookieDomain();
    
    let cookie = `${COOKIE_NAME}=${locale};max-age=${maxAge};path=/;SameSite=Lax`;
    if (domain) {
      cookie += `;domain=${domain}`;
    }
    if (window.location.protocol === 'https:') {
      cookie += ';Secure';
    }
    
    document.cookie = cookie;
  } catch (e) {
    console.warn('[LocaleManager] Cookie write failed:', e);
  }
}

function getLocaleCookie() {
  if (typeof document === 'undefined') return null;
  
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
    return match ? match[1] : null;
  } catch (e) {
    return null;
  }
}

// =============================================================================
// URL MANAGEMENT
// =============================================================================

/**
 * Get locale from URL ?lang= parameter
 */
export function getLocaleFromUrl() {
  if (typeof window === 'undefined') return null;
  
  try {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');
    return normalizeLocale(lang);
  } catch (e) {
    return null;
  }
}

// Alias
export const getLanguageFromUrl = getLocaleFromUrl;

/**
 * Update URL with locale parameter (optional - can remove ?lang after applying)
 */
export function setLocaleInUrl(locale, replace = true) {
  if (typeof window === 'undefined' || !locale) return;
  
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('lang', locale);
    
    if (replace) {
      window.history.replaceState({}, '', url.toString());
    } else {
      window.history.pushState({}, '', url.toString());
    }
  } catch (e) {
    console.warn('[LocaleManager] URL update failed:', e);
  }
}

/**
 * Remove ?lang= from URL after applying (cleaner URLs)
 */
export function clearLocaleFromUrl() {
  if (typeof window === 'undefined') return;
  
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('lang')) {
      url.searchParams.delete('lang');
      window.history.replaceState({}, '', url.toString());
    }
  } catch (e) {}
}

// Alias
export const setLanguageInUrl = setLocaleInUrl;

// =============================================================================
// BROWSER LANGUAGE DETECTION
// =============================================================================

function getBrowserLocale() {
  if (typeof navigator === 'undefined') return null;
  
  try {
    const languages = navigator.languages || [navigator.language];
    
    for (const lang of languages) {
      const normalized = normalizeLocale(lang);
      if (normalized) {
        return normalized;
      }
    }
  } catch (e) {}
  
  return null;
}

// =============================================================================
// CORE LOCALE MANAGEMENT
// =============================================================================

/**
 * DETECT LOCALE - Canonical resolver with strict priority
 * 1) URL ?lang=xx
 * 2) localStorage: keikka_lang
 * 3) Cookie: keikka_lang
 * 4) Browser language
 * 5) Default: en
 */
export function detectLocale() {
  // 1) URL param (highest priority - deep link override)
  const urlLocale = getLocaleFromUrl();
  if (urlLocale) {
    return urlLocale;
  }
  
  // 2) localStorage (user's explicit choice)
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const normalized = normalizeLocale(stored);
    if (normalized) {
      return normalized;
    }
    // Also check old keys for migration
    const oldKeys = ['keikka.locale', 'keikka_locale', 'i18nextLng'];
    for (const key of oldKeys) {
      const old = localStorage.getItem(key);
      const oldNorm = normalizeLocale(old);
      if (oldNorm) {
        return oldNorm;
      }
    }
  } catch (e) {}
  
  // 3) Cookie
  const cookieLocale = normalizeLocale(getLocaleCookie());
  if (cookieLocale) {
    return cookieLocale;
  }
  
  // 4) Browser language (first visit)
  const browserLocale = getBrowserLocale();
  if (browserLocale) {
    return browserLocale;
  }
  
  // 5) Default
  return DEFAULT_LANGUAGE;
}

// Alias
export const detectLanguage = detectLocale;

/**
 * PERSIST LOCALE to all storage mechanisms
 */
function persistLocale(locale) {
  if (!locale) return;
  
  // 1. In-memory
  currentLocale = locale;
  
  // 2. localStorage (canonical key)
  try {
    localStorage.setItem(STORAGE_KEY, locale);
    
    // Clean up old keys
    const oldKeys = ['keikka.locale', 'keikka_locale', 'i18nextLng'];
    oldKeys.forEach(key => {
      try { localStorage.removeItem(key); } catch (e) {}
    });
  } catch (e) {
    console.warn('[LocaleManager] localStorage write failed:', e);
  }
  
  // 3. Cookie
  setLocaleCookie(locale);
  
  // 4. Update HTML attributes
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
    document.documentElement.dir = getTextDirection(locale);
    
    // Update or create meta tag
    let meta = document.querySelector('meta[http-equiv="content-language"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('http-equiv', 'content-language');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', locale);
  }
}

/**
 * BROADCAST locale change to other tabs (within 200ms)
 * Uses BOTH BroadcastChannel AND localStorage event
 */
function broadcastLocaleChange(locale, source = 'user') {
  // 1. BroadcastChannel (primary - instant)
  if (broadcastChannel) {
    try {
      broadcastChannel.postMessage({ 
        type: 'LOCALE_CHANGE', 
        locale,
        source,
        timestamp: Date.now() 
      });
    } catch (e) {}
  }
  
  // 2. localStorage triggers storage event in other tabs (fallback)
  // Already handled by persistLocale writing to localStorage
  
  // 3. Custom event for same-tab listeners
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('keikka-locale-change', { 
      detail: { locale, source } 
    }));
  }
}

/**
 * GET current locale
 */
export function getLocale() {
  if (!currentLocale) {
    currentLocale = detectLocale();
    persistLocale(currentLocale);
  }
  return currentLocale;
}

// Alias
export const getLanguage = getLocale;

/**
 * SET locale - THE ONLY way to change locale
 */
export function setLocale(locale, options = {}) {
  const { updateUrl = false, broadcast = true, force = false, clearUrlParam = false } = options;
  
  const normalized = normalizeLocale(locale);
  if (!normalized) {
    console.warn('[LocaleManager] Invalid/unsupported locale:', locale);
    return false;
  }
  
  if (!force && normalized === currentLocale) {
    return true;
  }
  
  const oldLocale = currentLocale;
  
  persistLocale(normalized);
  
  if (updateUrl) {
    setLocaleInUrl(normalized);
  } else if (clearUrlParam) {
    clearLocaleFromUrl();
  }
  
  if (broadcast) {
    broadcastLocaleChange(normalized, 'user');
  }
  
  // Notify listeners
  changeListeners.forEach(listener => {
    try {
      listener(normalized, oldLocale);
    } catch (e) {
      console.warn('[LocaleManager] Listener error:', e);
    }
  });
  
  return true;
}

// Alias
export const setLanguage = setLocale;

/**
 * INITIALIZE - Call once on app startup
 */
export function initializeLocaleManager() {
  if (isInitialized) {
    return getLocale();
  }
  
  // Detect and persist locale
  const locale = detectLocale();
  persistLocale(locale);
  
  // If URL had ?lang=, optionally clear it after applying
  const urlLocale = getLocaleFromUrl();
  if (urlLocale) {
    // Keep URL param for sharing, but you can enable this to clean URLs:
    // clearLocaleFromUrl();
  }
  
  // Setup BroadcastChannel for cross-tab sync (instant, < 200ms)
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL);
      broadcastChannel.onmessage = (event) => {
        const { type, locale: newLocale, source } = event.data || {};
        if (type === 'LOCALE_CHANGE' && newLocale && newLocale !== currentLocale) {
          const normalized = normalizeLocale(newLocale);
          if (normalized) {
            // Update without re-broadcasting to prevent loops
            setLocale(normalized, { broadcast: false });
          }
        }
      };
    } catch (e) {
      console.warn('[LocaleManager] BroadcastChannel setup failed:', e);
    }
  }
  
  // Storage event fallback for cross-tab sync
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY && event.newValue) {
        const normalized = normalizeLocale(event.newValue);
        if (normalized && normalized !== currentLocale) {
          setLocale(normalized, { broadcast: false });
        }
      }
    });
  }
  
  isInitialized = true;
  return locale;
}

// Alias
export const initializeI18n = initializeLocaleManager;

/**
 * SUBSCRIBE to locale changes
 */
export function subscribeToLocaleChanges(callback) {
  if (typeof callback === 'function') {
    changeListeners.push(callback);
    return () => {
      changeListeners = changeListeners.filter(l => l !== callback);
    };
  }
  return () => {};
}

// Alias
export const onLanguageChange = subscribeToLocaleChanges;

/**
 * CLEANUP
 */
export function cleanupLocaleManager() {
  if (broadcastChannel) {
    try {
      broadcastChannel.close();
    } catch (e) {}
    broadcastChannel = null;
  }
  changeListeners = [];
  isInitialized = false;
}

// Alias
export const cleanupI18n = cleanupLocaleManager;

// =============================================================================
// DEBUG INFO
// =============================================================================

export function getDebugInfo() {
  return {
    currentLocale,
    isInitialized,
    urlLocale: getLocaleFromUrl(),
    localStorageLocale: typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null,
    cookieLocale: getLocaleCookie(),
    browserLocale: getBrowserLocale(),
    documentLang: typeof document !== 'undefined' ? document.documentElement.lang : null,
    documentDir: typeof document !== 'undefined' ? document.documentElement.dir : null,
    supportedLocales: TIER_A_LANGUAGES,
    totalLanguages: SUPPORTED_LOCALES.length,
  };
}

// =============================================================================
// LEGACY COMPATIBILITY - Map old names to new
// =============================================================================

export const LOCALE_CONFIG = {
  STORAGE_KEY,
  COOKIE_NAME,
  DEFAULT_LOCALE: DEFAULT_LANGUAGE,
  FALLBACK_LOCALE: FALLBACK_LANGUAGE,
  PRIMARY_LOCALES: TIER_A_LANGUAGES,
  ALL_LOCALES: TIER_A_LANGUAGES,
  RTL_LOCALES: RTL_LANGUAGES,
  BROADCAST_CHANNEL,
};

export const LOCALE_INFO = Object.fromEntries(
  SUPPORTED_LOCALES.map(l => [l.code, {
    name: l.label,
    nativeName: l.nativeLabel,
    flag: l.flag,
    tier: 1,
  }])
);

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default {
  // Core
  getLocale,
  setLocale,
  detectLocale,
  initializeLocaleManager,
  subscribeToLocaleChanges,
  cleanupLocaleManager,
  
  // Utilities
  normalizeLocale,
  isLocaleSupported,
  isTierA,
  isPrimaryLocale,
  isRTL,
  getTextDirection,
  getLocaleInfo,
  searchLanguages,
  getLocaleFromUrl,
  setLocaleInUrl,
  clearLocaleFromUrl,
  
  // Debug
  getDebugInfo,
  
  // Constants
  SUPPORTED_LOCALES,
  ALL_LANGUAGES,
  TIER_A_LANGUAGES,
  RTL_LANGUAGES,
  DEFAULT_LANGUAGE,
  FALLBACK_LANGUAGE,
  LANGUAGE_MAP,
  STORAGE_KEY,
  COOKIE_NAME,
  LOCALE_CONFIG,
  LOCALE_INFO,
  
  // Aliases
  getLanguage: getLocale,
  setLanguage: setLocale,
  detectLanguage: detectLocale,
  initializeI18n: initializeLocaleManager,
  onLanguageChange: subscribeToLocaleChanges,
  cleanupI18n: cleanupLocaleManager,
  normalizeLanguage: normalizeLocale,
  getLanguageInfo: getLocaleInfo,
  getLanguageFromUrl: getLocaleFromUrl,
  setLanguageInUrl: setLocaleInUrl,
};
